/**
 * BE-09 / FE-08 — Nightly scheduler visibility and admin actions.
 * APIs: GET /api/scheduler/reports, POST dry-run, POST trigger.
 */
import React from "react";
import { Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase/firebase";
import styles from "../components/Dashboard.module.css";
import { datasetCoverageApi, type AgentDatasetPair } from "../services/datasetCoverageApi";
import {
  schedulerApi,
  SchedulerEntryOutcome,
  SchedulerConfig,
  SchedulerReport,
  SchedulerTriggeredBy,
} from "../services/schedulerApi";

const TRIGGER_LABELS: Record<SchedulerTriggeredBy, string> = {
  cron: "Cron (nightly)",
  manual: "Manual trigger",
  dry_run: "Dry-run",
};

const OUTCOME_LABELS: Record<SchedulerEntryOutcome, string> = {
  enqueued: "Enqueued",
  ready: "Ready",
  duplicate_run: "Skipped — duplicate run",
  agent_not_found: "Skipped — agent not found",
  agent_disabled: "Skipped — agent disabled",
  dataset_not_found: "Skipped — dataset not found",
  connector_unreachable: "Skipped — connector unreachable",
  error: "Error",
};

const pairLabel = (entry: { agentId: string; datasetId: string; datasetName?: string | null }) =>
  `${entry.agentId} + ${entry.datasetName || entry.datasetId}`;

const formatWhen = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString() : "—";

/** Match backend report scoping for the signed-in operator. */
const scopeReportToViewer = (report: SchedulerReport, viewerUid: string | null): SchedulerReport => {
  if (!viewerUid) return report;
  const entries = report.entries.filter((e) => e.ownerUid === viewerUid);
  let enqueued = 0;
  let skipped = 0;
  let failed = 0;
  for (const entry of entries) {
    if (entry.outcome === "enqueued") enqueued += 1;
    else if (entry.outcome === "error") failed += 1;
    else skipped += 1;
  }
  return {
    ...report,
    entries,
    totalPairs: entries.length,
    enqueued,
    skipped,
    failed,
  };
};

const SchedulerPage: React.FC = () => {
  const [reports, setReports] = React.useState<SchedulerReport[]>([]);
  const [schedulerConfig, setSchedulerConfig] = React.useState<SchedulerConfig | null>(null);
  const [nightlyPairs, setNightlyPairs] = React.useState<AgentDatasetPair[]>([]);
  const [expandedReportId, setExpandedReportId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [feedback, setFeedback] = React.useState<string | null>(null);
  const [signedInUid, setSignedInUid] = React.useState<string | null>(auth.currentUser?.uid ?? null);

  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => setSignedInUid(user?.uid ?? null));
    return () => unsub();
  }, []);

  const loadNightlyPairs = React.useCallback(async () => {
    const res = await datasetCoverageApi.listPairs();
    setNightlyPairs(res.data.filter((p) => p.nightly));
  }, []);

  const loadReports = React.useCallback(async () => {
    setError(null);
    const res = await schedulerApi.listReports({ limit: 20 });
    setReports(res.reports);
    if (res.reports.length > 0) {
      setExpandedReportId((prev) =>
        prev && res.reports.some((r) => r.reportId === prev) ? prev : res.reports[0].reportId,
      );
    }
    return res.reports;
  }, []);

  const loadConfig = React.useCallback(async () => {
    setSchedulerConfig(await schedulerApi.getConfig());
  }, []);

  const refreshAll = React.useCallback(async () => {
    await Promise.all([loadNightlyPairs(), loadReports(), loadConfig()]);
  }, [loadConfig, loadNightlyPairs, loadReports]);

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        await refreshAll();
      } catch (e: unknown) {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : "Failed to load scheduler data.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [refreshAll]);

  const runAction = async (action: "dry-run" | "trigger") => {
    setBusy(true);
    setError(null);
    setFeedback(null);
    try {
      const raw =
        action === "dry-run" ? await schedulerApi.dryRun() : await schedulerApi.triggerPass();
      const report = scopeReportToViewer(raw, signedInUid);
      await refreshAll();
      setExpandedReportId(report.reportId);
      const summary = `${TRIGGER_LABELS[report.triggeredBy]}: ${report.enqueued} enqueued, ${report.skipped} skipped, ${report.failed} failed (${report.totalPairs} nightly pairs).`;
      setFeedback(summary);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Scheduler action failed.");
    } finally {
      setBusy(false);
    }
  };

  const expandedReport = reports.find((r) => r.reportId === expandedReportId) ?? null;

  if (loading) {
    return (
      <div className={styles.dashboard}>
        <section className={styles.dashboardLoading} aria-live="polite" aria-busy="true">
          <div className={styles.dashboardLoadingSpinner} />
          <p className={styles.dashboardLoadingText}>Loading scheduler reports...</p>
        </section>
      </div>
    );
  }

  return (
    <div className={styles.diagnosticsPage}>
      <section className={styles.diagnosticsCard}>
        <h1 className={styles.diagnosticsTitle}>Nightly scheduler</h1>
        <p className={styles.diagnosticsSubtitle}>
          Dry-run, trigger, and the nightly cron each evaluate every agent–dataset pair below with nightly
          monitoring enabled (not a single pair picker). To monitor only one pair, enable nightly on that
          pair alone in <Link to="/agents">Agents</Link> → dataset coverage.
        </p>
        <p className={styles.diagnosticsSectionHint} data-testid="scheduler-cadence">
          Effective cadence: <code>{schedulerConfig?.cron ?? "unknown"}</code>{" "}
          ({schedulerConfig?.timezone ?? "UTC"}). Next execution:{" "}
          {formatWhen(schedulerConfig?.nextExecutionAt ?? null)}. Environment changes take effect after
          the worker restarts.
        </p>

        <h2 className={styles.diagnosticsSectionTitle}>Pairs included in the next pass</h2>
        <p className={styles.diagnosticsSectionHint}>
          Cron, dry-run, and trigger all use this same list.
        </p>
        <div className={styles.diagnosticsTableWrap}>
          <table className={styles.diagnosticsTable}>
            <thead>
              <tr>
                <th>Agent</th>
                <th>Dataset</th>
              </tr>
            </thead>
            <tbody>
              {nightlyPairs.length > 0 ? (
                nightlyPairs.map((pair) => (
                  <tr key={`${pair.agent_id}-${pair.dataset_id}`}>
                    <td>{pair.agent_id}</td>
                    <td>{pair.dataset_name || pair.dataset_id}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2}>
                    No nightly pairs configured. Open an agent in{" "}
                    <Link to="/agents">Agents</Link> and enable nightly on the dataset row you want monitored.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.agentsActions}>
          <button
            type="button"
            className={styles.metricsSecondaryBtn}
            onClick={() => void runAction("dry-run")}
            disabled={busy || nightlyPairs.length === 0}
            data-testid="scheduler-dry-run"
            title={nightlyPairs.length === 0 ? "Enable nightly on at least one pair first" : undefined}
          >
            {busy ? "Working…" : `Run dry-run (${nightlyPairs.length} pair${nightlyPairs.length === 1 ? "" : "s"})`}
          </button>
          <button
            type="button"
            className={styles.agentsPrimaryBtn}
            onClick={() => void runAction("trigger")}
            disabled={busy || nightlyPairs.length === 0}
            data-testid="scheduler-trigger"
            title={nightlyPairs.length === 0 ? "Enable nightly on at least one pair first" : undefined}
          >
            {busy ? "Working…" : `Trigger pass (${nightlyPairs.length} pair${nightlyPairs.length === 1 ? "" : "s"})`}
          </button>
          <button
            type="button"
            className={styles.metricsSecondaryBtn}
            onClick={() =>
              void refreshAll().catch((e: unknown) =>
                setError(e instanceof Error ? e.message : "Refresh failed."),
              )
            }
            disabled={busy}
            data-testid="scheduler-refresh"
          >
            Refresh
          </button>
        </div>

        {feedback ? <div className={styles.metricsSuccessBox}>{feedback}</div> : null}
        {error ? (
          <div className={styles.metricsErrorBox}>
            <p>{error}</p>
          </div>
        ) : null}
      </section>

      <section className={styles.diagnosticsCard}>
        <h2 className={styles.diagnosticsSectionTitle}>Recent scheduler reports</h2>
        <p className={styles.diagnosticsSectionHint}>
          <Link to="/runs?source=scheduler">View scheduler-created runs</Link>.
        </p>
        <div className={styles.diagnosticsTableWrap}>
          <table className={styles.diagnosticsTable}>
            <thead>
              <tr>
                <th>Report</th>
                <th>Trigger</th>
                <th>Started</th>
                <th>Pairs</th>
                <th>Enqueued</th>
                <th>Skipped</th>
                <th>Failed</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {reports.length > 0 ? (
                reports.map((report) => (
                  <tr key={report.reportId}>
                    <td>
                      <code>{report.reportId.slice(0, 8)}…</code>
                    </td>
                    <td>{TRIGGER_LABELS[report.triggeredBy]}</td>
                    <td>{formatWhen(report.startedAt)}</td>
                    <td>{report.totalPairs}</td>
                    <td>{report.enqueued}</td>
                    <td>{report.skipped}</td>
                    <td>{report.failed}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.metricsSecondaryBtn}
                        onClick={() =>
                          setExpandedReportId((id) =>
                            id === report.reportId ? null : report.reportId,
                          )
                        }
                        data-testid={`expand-report-${report.reportId}`}
                      >
                        {expandedReportId === report.reportId ? "Hide entries" : "Show entries"}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8}>No scheduler reports yet. Run a dry-run or wait for the nightly cron.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {expandedReport ? (
        <section className={styles.diagnosticsCard}>
          <h2 className={styles.diagnosticsSectionTitle}>
            Report entries — {TRIGGER_LABELS[expandedReport.triggeredBy]}
          </h2>
          <p className={styles.diagnosticsSectionHint}>
            Completed {formatWhen(expandedReport.completedAt)} · {expandedReport.entries.length}{" "}
            pair(s) evaluated
          </p>
          <div className={styles.diagnosticsTableWrap}>
            <table className={styles.diagnosticsTable}>
              <thead>
                <tr>
                  <th>Pair</th>
                  <th>Outcome</th>
                  <th>Run ID</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {expandedReport.entries.length > 0 ? (
                  expandedReport.entries.map((entry, idx) => (
                    <tr key={`${entry.agentId}-${entry.datasetId}-${idx}`}>
                      <td>{pairLabel(entry)}</td>
                      <td>{OUTCOME_LABELS[entry.outcome]}</td>
                      <td>
                        {entry.runId ? (
                          <Link to="/runs?source=scheduler">{entry.runId.slice(0, 12)}…</Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{entry.reason || "—"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>No nightly pairs were evaluated in this pass.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default SchedulerPage;
